import { useEffect, useState } from "react";
import { getConversations, getPhoneCalls } from "../../../services/conversation_service";
import { Modal } from "@iq/models";
import "../Modal.css";

function formatCallTime(value) {
    if (!value) {
        return "-";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return date.toLocaleString([], {
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function DealConversationsModal({ isOpen, onClose, dealId, contactId, contactName }) {
    const [phoneCalls, setPhoneCalls] = useState([]);
    const [emailConversations, setEmailConversations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [phoneError, setPhoneError] = useState("");
    const [emailError, setEmailError] = useState("");

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        let cancelled = false;
        setLoading(true);
        setPhoneError("");
        setEmailError("");
        setPhoneCalls([]);
        setEmailConversations([]);

        Promise.allSettled([
            getPhoneCalls({ dealId, contactId }),
            getConversations({ dealId, contactId, pageSize: 100 }),
        ]).then(([phoneResult, emailResult]) => {
            if (cancelled) {
                return;
            }

            if (phoneResult.status === "fulfilled") {
                setPhoneCalls(Array.isArray(phoneResult.value?.calls) ? phoneResult.value.calls : []);
            } else {
                setPhoneError(phoneResult.reason?.message || "Failed to load phone conversations");
            }

            if (emailResult.status === "fulfilled") {
                setEmailConversations(Array.isArray(emailResult.value?.conversations) ? emailResult.value.conversations : []);
            } else {
                setEmailError(emailResult.reason?.message || "Failed to load email conversations");
            }
        }).finally(() => {
            if (!cancelled) {
                setLoading(false);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [isOpen, dealId, contactId]);

    if (!isOpen) {
        return null;
    }

    const titleLabel = dealId ? `Deal #${dealId} Conversations` : "Deal Conversations";

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={titleLabel} overlayClassName="modal-backdrop" contentClassName="modal-card modal-card-wide">
                    <div className="modal-header">
                        <div className="form-field">
                        <label><b>Deal</b></label>
                        <div>{dealId ? String(dealId) : "No deal linked"}</div>
                    </div>
                    <div className="form-field">
                        <label><b>Contact</b></label>
                        <div>{contactName || "No contact linked"}</div>
                    </div>
                </div>

               

                {loading ? <div className="conversation-empty">Loading conversations...</div> : null}
                {!loading && (phoneError || emailError) ? (
                    <div className="conversation-empty" style={{ color: "red" }}>
                        {phoneError || emailError}
                    </div>
                ) : null}
                {!loading && !phoneError && !emailError && phoneCalls.length === 0 && emailConversations.length === 0 ? (
                    <div className="conversation-empty">No phone or email conversations found for this deal.</div>
                ) : null}

                {!loading && phoneCalls.length > 0 ? (
                    <>
                        <h3 style={{ textAlign: "center" }}>Phone conversations</h3>
                        <div className="phone-call-rows">
                            {phoneCalls.map((call) => (
                                <article key={String(call.id)} className="phone-call-row">
                                    <div className="phone-call-row-main">
                                        <div className="phone-call-row-title">
                                            {call.deal_title || "No deal linked"}
                                        </div>
                                        <div className="phone-call-row-subtitle">
                                            {call.user_name || "Unknown user"} · {String(call.direction || "outbound").toUpperCase()} · {String(call.call_type || "call").toUpperCase()}
                                        </div>
                                        <div className="phone-call-row-contact">
                                            Contact: {call.contact_name || call.manual_contact_name || call.contact_phone || "Unknown"}
                                        </div>
                                        <div className="phone-call-row-notes">{call.notes || "No notes"}</div>
                                    </div>
                                    <div className="phone-call-row-side">
                                        <span>{formatCallTime(call.call_at)}</span>
                                        <span>{Math.max(Number(call.duration_seconds) || 0, 0)}s</span>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </>
                ) : null}

                {!loading && emailConversations.length > 0 ? (
                    <>
                        <h3 style={{ marginTop: "1.5rem" }}>Email conversations</h3>
                        <div className="phone-call-rows">
                            {emailConversations.map((thread) => (
                                <article key={String(thread.thread_id)} className="phone-call-row">
                                    <div className="phone-call-row-main">
                                        <div className="phone-call-row-title">
                                            {thread.subject || "No subject"}
                                        </div>
                                        <div className="phone-call-row-subtitle">
                                            {thread.contact_name || thread.contact_email || "Unknown contact"} · {String(thread.status || "").toUpperCase()}
                                        </div>
                                        <div className="phone-call-row-contact">
                                            Messages: {thread.message_count || 0}
                                        </div>
                                        <div className="phone-call-row-notes">
                                            Thread ID: {thread.thread_id}
                                        </div>
                                    </div>
                                    <div className="phone-call-row-side">
                                        <span>{formatCallTime(thread.updated_at)}</span>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </>
                ) : null}
        </Modal>
    );
}

export default DealConversationsModal;
